import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Sparkles, Brain, Edit, Trash2, Calendar, Clock, User, Search, Filter, IndianRupee, Target, FileSpreadsheet, History, Phone, Mail, MessageSquare, CheckCircle, ArrowRight, AlertTriangle, RefreshCw, MapPin, TrendingUp, BarChart3, Loader2, Table2, LayoutList, LayoutGrid, Columns3, Eye, ChevronUp, ChevronDown, MessageCircle, ChevronRight, Download } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { formatDistanceToNow, format, isPast, parseISO } from "date-fns";
import LeadImportDialog from "@/components/LeadImportDialog";
import { CustomFieldsRenderer } from "@/components/leads/CustomFieldsRenderer";
import { ColumnCustomizer, SortableColumnHeader } from "@/components/leads/ColumnCustomizer";
import { LeadFormFields } from "@/components/leads/LeadFormFields";
import { FullScreenLoader, TableSkeleton, ListSkeleton, GridSkeleton, QuickViewSkeleton } from "@/components/GlobalLoader";
import { useBusiness } from "@/contexts/BusinessContext";
import MobileLeadsA from "@/components/mobile/MobileLeadsA";
import LeadsKanbanView from "@/components/leads/LeadsKanbanView";
import { DATE_PRESETS, computeDatePreset } from "@/utils/datePresets";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Lead sources list - will be overridden by API data
const FALLBACK_LEAD_SOURCES = [
  "Facebook", "Facebook Ads", "Google", "Google Ads", "WhatsApp",
  "Website", "Instagram", "LinkedIn", "Referral", "Walk-in", "Manual", "Google Sheet"
];

// Default source tabs to show
const DEFAULT_SOURCE_TABS = [
  { key: "all", label: "All Leads", icon: "📊" },
  { key: "Facebook", label: "Facebook", icon: "📘" },
  { key: "Google Sheet", label: "Google Sheet", icon: "📗" },
  { key: "Website", label: "Website", icon: "🌐" },
  { key: "WhatsApp", label: "WhatsApp", icon: "💬" },
  { key: "Manual", label: "Manual", icon: "✍️" },
  { key: "Google Ads", label: "Google Ads", icon: "📢" }
];

// Pull the company name from any of the places it may live across imports:
//   - lead.company         (manual create + Excel import using "company" column)
//   - lead.company_name    (category templates that store company under "company_name")
//   - customValues.company_name / company  (custom-field-driven imports)
function getCompany(lead, customValues) {
  return (
    lead?.company ||
    lead?.company_name ||
    customValues?.company_name ||
    customValues?.company ||
    ""
  );
}

// Dynamic table cell renderer
function DynamicCell({ colKey, lead, stageColor, sourceColor, customValues, onViewLead }) {
  switch (colKey) {
    case "name": {
      // Fall back to custom name fields if core name is empty
      const displayName = lead.name || lead.patient_name || lead.student_name || lead.company_name || lead.parent_name || customValues?.patient_name || customValues?.student_name || customValues?.company_name || customValues?.name || lead.phone || "Unnamed";
      return (
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onViewLead(lead.lead_id); }}
              className={`font-medium hover:text-blue-600 hover:underline text-left text-blue-700 ${lead.is_missed ? "text-red-700" : ""}`}
              data-testid={`lead-name-link-${lead.lead_id}`}
            >
              {displayName}
            </button>
            {lead.is_missed && <AlertTriangle className="w-4 h-4 text-red-500" />}
          </div>
          {getCompany(lead, customValues) && (
            <p className="text-xs text-slate-500">{getCompany(lead, customValues)}</p>
          )}
        </td>
      );
    }
    case "phone":
      return (
        <td className="px-4 py-3 text-sm text-slate-600">
          {lead.phone ? <a href={`tel:${lead.phone}`} className="hover:text-indigo-600">{lead.phone}</a> : "-"}
        </td>
      );
    case "email":
      return (
        <td className="px-4 py-3 text-sm text-slate-600">
          {lead.email ? <a href={`mailto:${lead.email}`} className="hover:text-indigo-600 truncate max-w-[200px] block">{lead.email}</a> : "-"}
        </td>
      );
    case "remark":
      return (
        <td className="px-4 py-3 text-sm text-slate-600">
          <p className="truncate max-w-[200px]" title={lead.remark || lead.notes || ""}>{lead.remark || lead.notes || "-"}</p>
        </td>
      );
    case "source":
      return (
        <td className="px-4 py-3">
          {lead.source ? <Badge className={`text-xs ${sourceColor[lead.source] || "bg-slate-100 text-slate-600"}`}>{lead.source}</Badge> : "-"}
        </td>
      );
    case "stage":
      return (
        <td className="px-4 py-3">
          <Badge className={`text-xs ${stageColor[lead.stage] || "bg-slate-100 text-slate-600"}`}>{lead.stage}</Badge>
        </td>
      );
    case "assigned_to_name":
      return <td className="px-4 py-3 text-sm text-slate-600">{lead.assigned_to_name || lead.created_by_name || "-"}</td>;
    case "created_at":
      return <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{lead.created_at ? format(new Date(lead.created_at), "MMM d, yyyy h:mm a") : "-"}</td>;
    case "updated_at":
      return <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{lead.updated_at ? format(new Date(lead.updated_at), "MMM d, yyyy h:mm a") : "-"}</td>;
    case "city":
      return <td className="px-4 py-3 text-sm text-slate-600">{lead.city || "-"}</td>;
    case "company":
      return <td className="px-4 py-3 text-sm text-slate-600">{getCompany(lead, customValues) || "-"}</td>;
    case "deal_value":
      return <td className="px-4 py-3 text-sm text-slate-600">{lead.deal_value ? `${lead.deal_value}` : "-"}</td>;
    case "campaign":
      return <td className="px-4 py-3 text-sm text-slate-600">{lead.campaign || "-"}</td>;
    default:
      // Custom field - check lead object first, then customValues
      return (
        <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
          {lead[colKey] || customValues?.[colKey] || "-"}
        </td>
      );
  }
}

// Parse email body into structured fields for quick view
function parseEmailFields(text) {
  if (!text) return null;
  // Clean the text
  let cleaned = text.replace(/<[^>]+>/g, " ");
  cleaned = cleaned.replace(/sent from\s+(my\s+)?(iphone|android|samsung|mobile|mail|outlook|gmail|yahoo)[\s\S]*/gi, "");
  cleaned = cleaned.replace(/^[^*\n]*\|[^*\n]*$/gm, "");
  cleaned = cleaned.replace(/\|/g, " ").replace(/\s{2,}/g, " ");
  
  const lines = cleaned.split(/\r?\n/).map(l => l.trim()).filter(l => l && l.length > 1);
  const fields = [];
  
  for (const line of lines) {
    // *Key* Value
    const m1 = line.match(/^\*([^*]+)\*\s*[:\-–]?\s*(.+)$/);
    if (m1) { fields.push({ label: m1[1].trim(), value: m1[2].trim() }); continue; }
    // Key: Value
    const m2 = line.match(/^([A-Za-z][A-Za-z0-9 _/&.()-]{0,40})\s*[:\-–]\s+(.+)$/);
    if (m2 && m2[1].trim().length >= 2 && m2[1].trim().split(" ").length <= 5) { fields.push({ label: m2[1].trim(), value: m2[2].trim() }); continue; }
    // Known field
    const m3 = line.match(/^(Name|Phone\s*(?:No\.?|Number)?|Email|Course[s]?|NEET\s*Score|Score|City|Current\s*City|State|Qualification|DOB|Gender|College|Message|Query)\s*[:\-–.]?\s+(.+)$/i);
    if (m3) { fields.push({ label: m3[1].trim(), value: m3[2].trim() }); continue; }
  }
  
  return fields.length >= 2 ? fields : null;
}

// Quick View expanded row component - lazy loads latest note, remark, email body
function QuickViewRow({ lead, colSpan }) {
  const [extraData, setExtraData] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [notesRes, remarksRes, sourcesRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/leads/${lead.lead_id}/notes`, { credentials: "include" }).then(r => r.ok ? r.json() : { notes: [] }),
          fetch(`${BACKEND_URL}/api/leads/${lead.lead_id}/remarks`, { credentials: "include" }).then(r => r.ok ? r.json() : []),
          fetch(`${BACKEND_URL}/api/leads/${lead.lead_id}/sources`, { credentials: "include" }).then(r => r.ok ? r.json() : { sources: [] }),
        ]);
        if (cancelled) return;

        const latestNote = (notesRes.notes || [])[0];
        const latestRemark = Array.isArray(remarksRes) ? remarksRes[0] : null;

        // Extract email body from lead_sources and parse it
        let emailBody = null;
        let emailParsedFields = null;
        for (const src of (sourcesRes.sources || [])) {
          const rp = src.raw_payload || {};
          if (rp.body_clean || rp.body_html) {
            const rawBody = rp.body_clean || rp.body_html?.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
            emailBody = rawBody;
            // Try to parse structured fields from the email
            emailParsedFields = parseEmailFields(rawBody);
            break;
          }
        }

        setExtraData({ latestNote, latestRemark, emailBody, emailParsedFields });
      } catch {
        setExtraData({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lead.lead_id, BACKEND_URL]);

  const items = [];

  if (lead.city) items.push({ label: "City", value: lead.city, icon: MapPin });
  if (!loading && extraData) {
    if (extraData.latestNote) items.push({ label: "Latest Note", value: extraData.latestNote.text || extraData.latestNote.content, icon: MessageSquare });
    else if (lead.notes) items.push({ label: "Notes", value: lead.notes, icon: MessageSquare });

    if (extraData.latestRemark) items.push({ label: "Latest Remark", value: extraData.latestRemark.text, icon: MessageCircle });
    else if (lead.remark) items.push({ label: "Remark", value: lead.remark, icon: MessageCircle });

    // Add parsed email fields as individual items, or fallback to raw body
    if (extraData.emailParsedFields) {
      items.push({ label: "Email Fields", value: extraData.emailParsedFields, icon: Mail, type: "parsed" });
    } else if (extraData.emailBody) {
      items.push({ label: "Email Body", value: extraData.emailBody, icon: Mail });
    }
  }

  return (
    <tr data-testid={`quick-view-${lead.lead_id}`}>
      <td colSpan={colSpan} className="p-0">
        <div
          className="border-l-4 border-indigo-500 bg-slate-50/80 dark:bg-slate-800/50 px-6 py-4"
          style={{ animation: "quickViewSlide 200ms ease-out" }}
        >
          <p className="text-xs font-semibold text-indigo-600 mb-3 flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> Capture Details
          </p>
          {loading ? (
            <QuickViewSkeleton />
          ) : items.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No capture details available</p>
          ) : (
            <div className="space-y-2.5">
              {items.map(f => {
                const Icon = f.icon;
                if (f.type === "parsed") {
                  // Render parsed email fields in a mini grid
                  return (
                    <div key={f.label} className="min-w-0" data-testid="qv-field-email-fields">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium flex items-center gap-1 mb-1.5">
                        <Icon className="w-3 h-3" /> Email Content
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 bg-white/80 rounded-lg border border-slate-200/80 px-3 py-2">
                        {f.value.slice(0, 6).map((field, idx) => (
                          <div key={idx}>
                            <p className="text-[10px] text-slate-400 font-medium">{field.label}</p>
                            <p className="text-xs text-slate-700 truncate" title={field.value}>{field.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={f.label} className="min-w-0" data-testid={`qv-field-${f.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium flex items-center gap-1 mb-0.5">
                      <Icon className="w-3 h-3" /> {f.label}
                    </p>
                    <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed whitespace-pre-line line-clamp-3" title={f.value}>
                      {f.value}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

const Leads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  // Export modal state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("xlsx");
  const [exportColumns, setExportColumns] = useState([]);
  const [exportAvailableColumns, setExportAvailableColumns] = useState([]);
  const [exporting, setExporting] = useState(false);
  // View state with localStorage persistence - default to "table"
  const [view, setView] = useState(() => {
    const savedView = localStorage.getItem("leads_view_preference");
    return savedView || "table";
  });
  // Force a card-style layout on phones — the data-dense table is unusable on small screens.
  const [isMobileViewport, setIsMobileViewport] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const effectiveView = (isMobileViewport && view === "table") ? "list" : view;
  const [editingLead, setEditingLead] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  
  // Bulk selection state for table view
  const [selectedLeads, setSelectedLeads] = useState([]);
  
  // Table sorting state
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Business context for filtering
  const { selectedBusiness, businessId } = useBusiness();
  
  // Sync All Sources state
  const [syncAllLoading, setSyncAllLoading] = useState(false);
  const [showSyncResult, setShowSyncResult] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [emailIntegrations, setEmailIntegrations] = useState([]);
  
  // Expanded row quick view
  const [expandedRowId, setExpandedRowId] = useState(null);
  
  // Timeline/Activity state
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  // Navigate to lead details
  const handleViewLead = (leadId) => {
    navigate(`/leads/${leadId}`);
  };
  const [showTimeline, setShowTimeline] = useState(false);
  const [newActivity, setNewActivity] = useState({
    action_type: "call",
    description: "",
    remarks: ""
  });

  // Lead Remarks state
  const [remarks, setRemarks] = useState([]);
  const [showRemarks, setShowRemarks] = useState(false);
  const [newRemark, setNewRemark] = useState("");
  const [loadingRemarks, setLoadingRemarks] = useState(false);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  
  // Source tabs state
  const [sourceCounts, setSourceCounts] = useState({});
  const [sourceTabsLoading, setSourceTabsLoading] = useState(false);
  const [todayStats, setTodayStats] = useState({ total: 0, bySource: {} });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const leadsPerPage = 50;
  
  // Business users for filter
  const [businessUsers, setBusinessUsers] = useState([]);
  
  // Dynamic lead sources from API (includes colors)
  const [dynamicSources, setDynamicSources] = useState([]);
  const [dynamicSourceConfigs, setDynamicSourceConfigs] = useState([]);

  // Dynamic pipeline stages from API
  const [pipelineStages, setPipelineStages] = useState([]);

  // Computed lead sources - prefer API data, fallback to defaults
  const LEAD_SOURCES = useMemo(() => {
    return dynamicSources.length > 0 ? dynamicSources : FALLBACK_LEAD_SOURCES;
  }, [dynamicSources]);
  
  // Date filter state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Highlighted preset pill; cleared when the user manually edits a date input.
  const [activeDatePreset, setActiveDatePreset] = useState("");
  
  // Custom fields for table columns
  const [tableCustomFields, setTableCustomFields] = useState([]);
  const [allLeadCustomValues, setAllLeadCustomValues] = useState({});
  
  // Column preferences state
  const [columnPrefs, setColumnPrefs] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(["name", "phone", "remark", "source", "stage", "assigned_to_name", "created_at"]);
  const [columnOrder, setColumnOrder] = useState(["name", "phone", "remark", "source", "stage", "assigned_to_name", "created_at"]);
  const [allFieldsDef, setAllFieldsDef] = useState([]);
  
  const [newLead, setNewLead] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    city: "",
    stage: "New Lead",
    source: "",
    deal_value: "",
    notes: "",
    next_meeting: "",
    meeting_notes: "",
  });
  const [customFieldValues, setCustomFieldValues] = useState({});
  
  // Meeting action dialog state
  const [meetingActionOpen, setMeetingActionOpen] = useState(false);
  const [actionLead, setActionLead] = useState(null);
  const [meetingActionType, setMeetingActionType] = useState("complete");
  const [actionRemarks, setActionRemarks] = useState("");
  const [rescheduleDate, setRescheduleDate] = useState("");
  
  // Count missed meetings for header alert
  const missedCount = useMemo(() => {
    return leads.filter(lead => lead.is_missed).length;
  }, [leads]);

  useEffect(() => {
    fetchLeads();
    fetchUserRole();
    fetchBusinessUsers();
    fetchLeadSources();
    fetchSourceCounts();
    fetchPipelineStages();
  }, [startDate, endDate, businessId, sourceFilter, currentPage]);

  // Fetch custom fields for table columns + column preferences
  useEffect(() => {
    (async () => {
      try {
        const [fieldsRes, prefsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/lead-fields`, { credentials: "include" }),
          fetch(`${BACKEND_URL}/api/lead-table-preferences`, { credentials: "include" })
        ]);
        if (fieldsRes.ok) {
          const fields = await fieldsRes.json();
          setTableCustomFields(fields.filter(f => !f.is_system && f.is_active && f.show_in_table));
        }
        if (prefsRes.ok) {
          const prefs = await prefsRes.json();
          setColumnPrefs(prefs);
          setVisibleColumns(prefs.columns || ["name", "phone", "remark", "source", "stage", "assigned_to_name", "created_at"]);
          setColumnOrder(prefs.column_order || prefs.columns || ["name", "phone", "remark", "source", "stage", "assigned_to_name", "created_at"]);
          setAllFieldsDef([...(prefs.system_columns || []), ...(prefs.custom_columns || [])]);
        }
      } catch (e) { console.error("Failed to fetch fields/prefs", e); }
    })();
  }, [businessId]);

  // Fetch custom field values for visible leads when ANY non-system column is
  // visible — OR when "company" is visible (because category-templated leads
  // store their company under a custom field `company_name`, not lead.company).
  const hasCustomColumns = useMemo(() => {
    const sysKeys = new Set(["name", "phone", "email", "remark", "source", "stage", "assigned_to_name", "created_at", "updated_at", "city", "company", "deal_value", "campaign"]);
    if (visibleColumns.some(k => !sysKeys.has(k))) return true;
    if (visibleColumns.includes("company")) return true;
    return false;
  }, [visibleColumns]);
  
  useEffect(() => {
    if (!hasCustomColumns || leads.length === 0) return;
    (async () => {
      try {
        const leadIds = leads.map(l => l.lead_id);
        const res = await fetch(`${BACKEND_URL}/api/leads/batch-custom-values`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ lead_ids: leadIds })
        });
        if (res.ok) setAllLeadCustomValues(await res.json());
      } catch (e) { console.error("Failed to fetch custom values", e); }
    })();
  }, [leads, hasCustomColumns]);
  
  // Fetch source counts for tabs
  const fetchSourceCounts = async () => {
    try {
      let url = `${BACKEND_URL}/api/leads/sources`;
      if (businessId) {
        url += `?business_id=${businessId}`;
      }
      const response = await fetch(url, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Convert sources array to counts object
        const counts = { all: data.total };
        data.sources.forEach(item => {
          counts[item.source] = item.count;
        });
        setSourceCounts(counts);
        setTodayStats({
          total: data.today_total,
          bySource: data.today_by_source || {}
        });
      }
    } catch (error) {
      console.error("Error fetching source counts:", error);
    }
  };
  
  // Fetch users in the business for the "Leads by Users" filter
  const fetchBusinessUsers = async () => {
    try {
      // Get users from the team - available for admin/super_admin/manager
      const url = businessId 
        ? `${BACKEND_URL}/api/team/members?business_id=${businessId}`
        : `${BACKEND_URL}/api/team/members`;
      const response = await fetch(url, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBusinessUsers(data || []);
      }
    } catch (error) {
      console.error("Error fetching business users:", error);
    }
  };

  // Fetch dynamic lead sources from API
  const fetchLeadSources = async () => {
    try {
      const url = businessId
        ? `${BACKEND_URL}/api/lead-sources/names?business_id=${businessId}`
        : `${BACKEND_URL}/api/lead-sources/names`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setDynamicSources(data.names || []);
        setDynamicSourceConfigs(data.sources || []);
      }
    } catch (err) {
      console.error("Error fetching lead sources:", err);
    }
  };
  
  const fetchPipelineStages = async () => {
    try {
      const url = businessId
        ? `${BACKEND_URL}/api/pipeline-stages?business_id=${businessId}`
        : `${BACKEND_URL}/api/pipeline-stages`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPipelineStages(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error fetching pipeline stages:", err);
    }
  };

  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const user = await response.json();
        setUserRole(user.role);
        setCurrentUserId(user.user_id);
        
        // Fetch Email integrations if user is admin or super_admin
        if (user.role === "admin" || user.role === "super_admin") {
          fetchEmailIntegrations();
        }
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  };
  
  // Fetch Email integrations
  const fetchEmailIntegrations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/email/integrations`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setEmailIntegrations(data.integrations || []);
      }
    } catch (error) {
      console.error("Error fetching email integrations:", error);
    }
  };
  
  // Sync all sources via unified backend endpoint
  const handleSyncAllSources = async () => {
    setSyncAllLoading(true);
    const results = { email: null, sheets: null, totalCreated: 0, totalUpdated: 0, errors: [] };
    try {
      const params = businessId ? `?business_id=${businessId}` : "";
      const res = await fetch(`${BACKEND_URL}/api/integrations/sync-all${params}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        results.totalCreated = data.total_new_leads || 0;
        results.totalUpdated = data.total_updated || 0;
        results.errors = data.errors || [];
        setSyncAllLoading(false);
        setSyncResult(results);
        setShowSyncResult(true);
        const sources = data.sources_synced || 0;
        if (results.totalCreated > 0 || results.totalUpdated > 0) {
          toast.success(`✅ ${results.totalCreated} new leads from ${sources} source${sources !== 1 ? "s" : ""}`);
        } else if (results.errors.length > 0) {
          toast.error(`Sync completed with ${results.errors.length} error(s)`);
        } else {
          toast.info("No new leads found across all sources");
        }
        fetchLeads();
        return;
      }
    } catch (e) { results.errors.push("Network error"); }
    // Fallback to legacy per-integration sync if sync-all failed

    // 1. Sync Email integrations
    const enabledEmails = emailIntegrations.filter(i => i.enabled);
    for (const integration of enabledEmails) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/email/integrations/${integration.integration_id}/sync`, {
          method: "POST", credentials: "include"
        });
        if (res.ok) {
          const r = await res.json();
          results.totalCreated += r.leads_created || 0;
        } else { results.errors.push(`Email: ${integration.email_address}`); }
      } catch (e) { results.errors.push(`Email: ${integration.email_address}`); }
    }

    // 2. Sync Google Sheets
    try {
      const sheetsRes = await fetch(`${BACKEND_URL}/api/integrations/simple-sheets`, { credentials: "include" });
      if (sheetsRes.ok) {
        const sheetsData = await sheetsRes.json();
        for (const sheet of (sheetsData.integrations || [])) {
          try {
            const syncRes = await fetch(`${BACKEND_URL}/api/integrations/simple-sheets/${sheet.integration_id}/sync`, {
              method: "POST", credentials: "include"
            });
            if (syncRes.ok) {
              const r = await syncRes.json();
              results.totalCreated += r.leads_created || 0;
              results.totalUpdated += r.leads_updated || 0;
            } else { results.errors.push(`Sheet: ${sheet.name}`); }
          } catch (e) { results.errors.push(`Sheet: ${sheet.name}`); }
        }
      }
    } catch (e) {}

    setSyncAllLoading(false);
    setSyncResult(results);
    setShowSyncResult(true);

    if (results.totalCreated > 0 || results.totalUpdated > 0) {
      toast.success(`Sync complete: ${results.totalCreated} created, ${results.totalUpdated} updated`);
    } else if (results.errors.length > 0) {
      toast.error(`Sync completed with ${results.errors.length} error(s)`);
    } else {
      toast.info("No new leads found across all sources");
    }

    fetchLeads();
  };
  
  // Fetch activities for a lead
  const fetchActivities = async (leadId) => {
    setLoadingActivities(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${leadId}/activities`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setActivities(data);
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoadingActivities(false);
    }
  };
  
  // Add a new activity
  const handleAddActivity = async () => {
    if (!newActivity.description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${editingLead.lead_id}/activities`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newActivity),
      });
      
      if (response.ok) {
        toast.success("Activity added successfully");
        setNewActivity({ action_type: "call", description: "", remarks: "" });
        fetchActivities(editingLead.lead_id);
      } else {
        toast.error("Failed to add activity");
      }
    } catch (error) {
      toast.error("Error adding activity");
    }
  };

  // Fetch remarks for a lead
  const fetchRemarks = async (leadId) => {
    setLoadingRemarks(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${leadId}/remarks`, { credentials: "include" });
      if (response.ok) setRemarks(await response.json());
    } catch (e) {
      console.error("Error fetching remarks:", e);
    } finally {
      setLoadingRemarks(false);
    }
  };

  // Add a new remark
  const handleAddRemark = async () => {
    if (!newRemark.trim()) { toast.error("Enter a remark"); return; }
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${editingLead.lead_id}/remarks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: newRemark }),
      });
      if (response.ok) {
        toast.success("Remark added");
        setNewRemark("");
        fetchRemarks(editingLead.lead_id);
      } else {
        toast.error("Failed to add remark");
      }
    } catch (e) {
      toast.error("Error adding remark");
    }
  };

  // Delete a remark
  const handleDeleteRemark = async (remarkId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${editingLead.lead_id}/remarks/${remarkId}`, {
        method: "DELETE", credentials: "include",
      });
      if (response.ok) { toast.success("Remark deleted"); fetchRemarks(editingLead.lead_id); }
    } catch (e) { toast.error("Error deleting remark"); }
  };
  
  // Handle meeting action (complete or reschedule)
  const handleMeetingAction = async () => {
    if (!actionLead) return;
    
    if (meetingActionType === "reschedule" && !rescheduleDate) {
      toast.error("Please select a new date for rescheduling");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${actionLead.lead_id}/meeting-action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          action: meetingActionType,
          remarks: actionRemarks || null,
          new_date: meetingActionType === "reschedule" ? rescheduleDate : null
        }),
      });
      
      if (response.ok) {
        toast.success(meetingActionType === "complete" ? "Meeting marked as completed!" : "Meeting rescheduled successfully!");
        setMeetingActionOpen(false);
        setActionLead(null);
        setActionRemarks("");
        setRescheduleDate("");
        fetchLeads(); // Refresh leads
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update meeting");
      }
    } catch (error) {
      toast.error("Error updating meeting");
    }
  };
  
  // Open meeting action dialog
  const openMeetingActionDialog = (lead, action, e) => {
    if (e) e.stopPropagation();
    setActionLead(lead);
    setMeetingActionType(action);
    setActionRemarks("");
    setRescheduleDate("");
    setMeetingActionOpen(true);
  };
  
  // Check if user can view lead timeline (Admin, Manager, or assigned/creator)
  const canViewTimeline = (lead) => {
    if (!userRole) return false;
    if (userRole === "admin" || userRole === "manager") return true;
    return lead.created_by === currentUserId || lead.assigned_to === currentUserId;
  };
  
  // Check if user can import (Super Admin, Admin, or Manager)
  const canImport = userRole === "super_admin" || userRole === "admin" || userRole === "manager";
  
  // Filtered leads based on search, source, and stage filter
  // Note: Most filtering is now done server-side, this is just for client-side search
  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      // Search filter - check name, email, phone (for additional client-side filtering)
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = searchQuery === "" || 
        lead.name?.toLowerCase().includes(searchLower) ||
        lead.email?.toLowerCase().includes(searchLower) ||
        lead.phone?.toLowerCase().includes(searchLower) ||
        lead.company?.toLowerCase().includes(searchLower);
      
      // User filter - filter by created_by or assigned_to user
      const matchesUser = userFilter === "all" || 
        lead.created_by === userFilter || 
        lead.assigned_to === userFilter;
      
      // Stage filter
      const matchesStage = stageFilter === "all" || lead.stage === stageFilter;
      
      return matchesSearch && matchesUser && matchesStage;
    });
  }, [leads, searchQuery, userFilter, stageFilter]);
  
  // Get lead counts by stage for the stage filter badges
  const stageCounts = useMemo(() => {
    const counts = { all: leads.length };
    leads.forEach((lead) => {
      counts[lead.stage] = (counts[lead.stage] || 0) + 1;
    });
    return counts;
  }, [leads]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      // Build URL with all filters
      let url = `${BACKEND_URL}/api/leads`;
      const params = new URLSearchParams();
      
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (sourceFilter && sourceFilter !== "all") params.append("source", sourceFilter);
      if (stageFilter && stageFilter !== "all") params.append("stage", stageFilter);
      if (userFilter && userFilter !== "all") params.append("assigned_to", userFilter);
      if (searchQuery) params.append("search", searchQuery);
      
      // Add business filter for admin users
      if (businessId) {
        params.append("business_id", businessId);
      }
      
      // Add pagination
      params.append("page", currentPage.toString());
      params.append("limit", leadsPerPage.toString());
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Handle both old format (array) and new format (object with leads array)
        if (Array.isArray(data)) {
          setLeads(data);
          setTotalLeads(data.length);
          setTotalPages(1);
        } else {
          setLeads(data.leads || []);
          setTotalLeads(data.total || 0);
          setTotalPages(data.total_pages || 1);
        }
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle source tab change
  const handleSourceTabChange = (source) => {
    setSourceFilter(source);
    setCurrentPage(1); // Reset to first page when changing source
  };

  // Handle view change with localStorage persistence
  const handleViewChange = (newView) => {
    setView(newView);
    localStorage.setItem("leads_view_preference", newView);
    setSelectedLeads([]); // Clear selection when changing views
  };
  
  // Kanban view: move a lead to a new pipeline stage via drag-and-drop.
  // Optimistic UI update, then PUT /api/leads/:id, rollback if the server rejects.
  const handleKanbanStageChange = async (lead, newStage) => {
    if (!lead || !newStage || lead.stage === newStage) return;
    const prev = lead.stage;
    setLeads(list => list.map(l => (l.lead_id === lead.lead_id ? { ...l, stage: newStage } : l)));
    try {
      const r = await fetch(`${BACKEND_URL}/api/leads/${lead.lead_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage: newStage }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Moved to ${newStage}`);
    } catch (e) {
      setLeads(list => list.map(l => (l.lead_id === lead.lead_id ? { ...l, stage: prev } : l)));
      toast.error("Failed to move lead");
    }
  };

  // Handle bulk select all
  const handleSelectAll = () => {
    if (selectedLeads.length === filteredLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads.map(lead => lead.lead_id));
    }
  };
  
  // Handle single lead selection
  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };
  
  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedLeads.length} lead(s)?`)) {
      return;
    }
    
    try {
      let successCount = 0;
      for (const leadId of selectedLeads) {
        const response = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
          method: "DELETE",
          credentials: "include",
        });
        if (response.ok) successCount++;
      }
      
      toast.success(`${successCount} lead(s) deleted successfully`);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error("Error deleting leads");
    }
  };
  
  // Table sorting handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Column customization handlers
  const handleColumnsChange = (newCols, newOrder, fields) => {
    setVisibleColumns(newCols);
    setColumnOrder(newOrder);
    if (fields) setAllFieldsDef(fields);
  };

  const handleHideColumn = async (key) => {
    const newCols = visibleColumns.filter(k => k !== key);
    const newOrder = columnOrder.filter(k => k !== key);
    setVisibleColumns(newCols);
    setColumnOrder(newOrder);
    try {
      await fetch(`${BACKEND_URL}/api/lead-table-preferences`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ columns: newCols, column_order: newOrder })
      });
    } catch (e) {}
  };

  const handleReplaceColumn = async (oldKey, newKey) => {
    const newCols = visibleColumns.map(k => k === oldKey ? newKey : k);
    const newOrder = columnOrder.map(k => k === oldKey ? newKey : k);
    setVisibleColumns(newCols);
    setColumnOrder(newOrder);
    try {
      await fetch(`${BACKEND_URL}/api/lead-table-preferences`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ columns: newCols, column_order: newOrder })
      });
    } catch (e) {}
  };

  // Build fieldMap for table rendering
  const fieldMap = useMemo(() => {
    const m = {};
    allFieldsDef.forEach(f => { m[f.key] = f; });
    return m;
  }, [allFieldsDef]);

  // Ordered visible columns for rendering
  const orderedVisibleColumns = useMemo(() => {
    return columnOrder.filter(k => visibleColumns.includes(k));
  }, [columnOrder, visibleColumns]);
  
  // Sort filtered leads for table view
  const sortedLeads = useMemo(() => {
    return [...filteredLeads].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      
      // Handle null/undefined
      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";
      
      // Handle dates
      if (sortColumn === "created_at" || sortColumn === "updated_at") {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      
      // String comparison
      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  }, [filteredLeads, sortColumn, sortDirection]);

  // Clear date filter
  const clearDateFilter = () => {
    setStartDate("");
    setEndDate("");
    setActiveDatePreset("");
  };

  const handleCreateLead = async () => {
    try {
      const leadData = {
        ...newLead,
        deal_value: newLead.deal_value ? parseFloat(newLead.deal_value) : null
      };
      const response = await fetch(`${BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(leadData),
      });
      if (response.ok) {
        const createdLead = await response.json();
        // Save custom field values if any
        if (Object.keys(customFieldValues).length > 0 && createdLead?.lead_id) {
          try {
            await fetch(`${BACKEND_URL}/api/leads/${createdLead.lead_id}/custom-values`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify(customFieldValues),
            });
          } catch (e) { console.error("Failed to save custom values", e); }
        }
        toast.success("Lead created successfully!");
        setDialogOpen(false);
        setNewLead({
          name: "",
          email: "",
          phone: "",
          company: "",
          city: "",
          stage: "New Lead",
          source: "",
          deal_value: "",
          notes: "",
          next_meeting: "",
          meeting_notes: "",
        });
        setCustomFieldValues({});
        fetchLeads();
      }
    } catch (error) {
      toast.error("Failed to create lead");
    }
  };

  const handleScoreLead = async (leadId) => {
    toast.info("AI is scoring the lead...");
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/score-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ lead_id: leadId }),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Lead scored: ${data.score}/100`);
        fetchLeads();
      }
    } catch (error) {
      toast.error("Failed to score lead");
    }
  };

  const handleSuggestFollowup = async (leadId) => {
    toast.info("AI is generating follow-up suggestion...");
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/suggest-followup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ lead_id: leadId }),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.suggestion, { duration: 8000 });
      }
    } catch (error) {
      toast.error("Failed to generate suggestion");
    }
  };

  const handleEditLead = (lead) => {
    // Format next_meeting for datetime-local input
    let formattedMeeting = "";
    if (lead.next_meeting) {
      try {
        const date = new Date(lead.next_meeting);
        formattedMeeting = date.toISOString().slice(0, 16);
      } catch {
        formattedMeeting = "";
      }
    }
    
    setEditingLead({
      lead_id: lead.lead_id,
      name: lead.name,
      email: lead.email || "",
      phone: lead.phone || "",
      company: lead.company || "",
      city: lead.city || "",
      stage: lead.stage,
      source: lead.source || "",
      deal_value: lead.deal_value || "",
      notes: lead.notes || "",
      next_meeting: formattedMeeting,
      meeting_notes: lead.meeting_notes || "",
      created_by: lead.created_by,
      assigned_to: lead.assigned_to,
    });
    setShowTimeline(false);
    setActivities([]);
    setRemarks([]);
    setShowRemarks(false);
    setNewRemark("");
    setEditDialogOpen(true);
    
    // Fetch activities when opening edit dialog
    if (canViewTimeline(lead)) {
      fetchActivities(lead.lead_id);
    }
    // Always fetch remarks
    fetchRemarks(lead.lead_id);
  };

  const handleUpdateLead = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${editingLead.lead_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: editingLead.name,
          email: editingLead.email,
          phone: editingLead.phone,
          company: editingLead.company,
          city: editingLead.city || null,
          stage: editingLead.stage,
          source: editingLead.source,
          deal_value: editingLead.deal_value ? parseFloat(editingLead.deal_value) : null,
          notes: editingLead.notes,
          next_meeting: editingLead.next_meeting || null,
          meeting_notes: editingLead.meeting_notes,
        }),
      });
      if (response.ok) {
        toast.success("Lead updated successfully!");
        fetchLeads();
        // Refresh activities after update
        fetchActivities(editingLead.lead_id);
      }
    } catch (error) {
      toast.error("Failed to update lead");
    }
  };

  const handleDeleteLead = async (leadId, leadName) => {
    if (!window.confirm(`Are you sure you want to delete "${leadName}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Lead deleted successfully!");
        fetchLeads();
      }
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const getLeadAge = (createdAt) => {
    try {
      return formatDistanceToNow(new Date(createdAt), { addSuffix: true });
    } catch {
      return "Unknown";
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "Unknown date";
    }
  };

  // Build dynamic stage color map from pipeline stages API
  const stageColor = useMemo(() => {
    // Fallback for legacy / unmapped stages
    const fallback = {
      "New Lead": "bg-sky-100 text-sky-700 border border-sky-200",
      "Contacted": "bg-blue-100 text-blue-700 border border-blue-200",
      "Qualified": "bg-indigo-100 text-indigo-700 border border-indigo-200",
      "Negotiation": "bg-amber-100 text-amber-700 border border-amber-200",
      "Follow-Up": "bg-orange-100 text-orange-700 border border-orange-200",
      "Follow-up": "bg-orange-100 text-orange-700 border border-orange-200",
      "Won": "bg-emerald-100 text-emerald-700 border border-emerald-200",
      "Won / Converted": "bg-emerald-100 text-emerald-700 border border-emerald-200",
      "Lost": "bg-red-100 text-red-700 border border-red-200",
      "On Hold": "bg-slate-100 text-slate-700 border border-slate-200",
    };
    // Map from color class to badge class (bg-sky-500 -> bg-sky-100 text-sky-700)
    const colorToBadge = (color) => {
      if (!color) return "bg-slate-100 text-slate-700 border border-slate-200";
      const match = color.match(/bg-(\w+)-\d+/);
      if (match) return `bg-${match[1]}-100 text-${match[1]}-700 border border-${match[1]}-200`;
      return "bg-slate-100 text-slate-700 border border-slate-200";
    };
    const colorMap = { ...fallback };
    for (const stage of pipelineStages) {
      if (stage.label) {
        colorMap[stage.label] = colorToBadge(stage.color);
      }
    }
    return colorMap;
  }, [pipelineStages]);

  // Build dynamic source color map from API config + fallbacks
  const sourceColor = useMemo(() => {
    const fallbackColors = {
      Facebook: "bg-blue-100 text-blue-700",
      "Facebook Ads": "bg-blue-100 text-blue-700",
      Google: "bg-red-100 text-red-700",
      "Google Ads": "bg-red-100 text-red-700",
      WhatsApp: "bg-green-100 text-green-700",
      Website: "bg-indigo-100 text-indigo-700",
      Instagram: "bg-pink-100 text-pink-700",
      LinkedIn: "bg-sky-100 text-sky-700",
      Referral: "bg-amber-100 text-amber-700",
      "Walk-in": "bg-teal-100 text-teal-700",
      Manual: "bg-slate-100 text-slate-700",
      Email: "bg-red-100 text-red-700",
      "Google Sheet": "bg-emerald-100 text-emerald-700",
      Webhook: "bg-purple-100 text-purple-700",
    };
    // Overlay colors from API config
    const colorMap = { ...fallbackColors };
    if (dynamicSourceConfigs && dynamicSourceConfigs.length > 0) {
      for (const src of dynamicSourceConfigs) {
        if (src.name && src.color && !colorMap[src.name]) {
          // Convert hex to a Tailwind-ish badge color
          colorMap[src.name] = `bg-slate-100 text-slate-700`;
          // Use the hex color as inline style via a special key
        }
      }
    }
    return colorMap;
  }, [dynamicSourceConfigs]);

  // Dynamic stages from API, with fallback
  const stages = useMemo(() => {
    if (pipelineStages.length > 0) {
      return pipelineStages.map(s => s.label);
    }
    return ["New Lead", "Contacted", "Qualified", "Negotiation", "Follow-Up", "Won", "Lost"];
  }, [pipelineStages]);

  const getLeadsByStage = (stage) => filteredLeads.filter((lead) => lead.stage === stage);

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return null;
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(value);
  };

  // ════════ Mobile-native admin leads view ════════
  // Same swap pattern as Dashboard: on <768px viewport AND admin/super_admin,
  // render the "Variation A · Vibrant" mobile design as a full takeover. The
  // mobile component bundles its own search/filter/Add Lead sheet so the
  // desktop dialog plumbing isn't needed here.
  if (isMobileViewport && (userRole === "admin" || userRole === "super_admin")) {
    return (
      <MobileLeadsA
        leads={filteredLeads}
        loading={loading}
        onLeadClick={handleViewLead}
        businessId={businessId}
        businessUsers={businessUsers}
        sources={(dynamicSourceConfigs || []).map(s => s.name).filter(Boolean)}
        onLeadCreated={() => fetchLeads()}
      />
    );
  }

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="leads-page">
      {/* Full Screen Loader for Sync All Sources */}
      <FullScreenLoader show={syncAllLoading} message="Syncing all sources... Please wait" />
      {missedCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="font-semibold text-red-800" data-testid="missed-leads-alert">
                {missedCount} Missed Follow-up{missedCount > 1 ? 's' : ''}
              </p>
              <p className="text-sm text-red-600">Action required - these meetings/follow-ups have passed their scheduled date</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStageFilter("all")}
            className="border-red-300 text-red-700 hover:bg-red-100 rounded-full"
          >
            View All Leads
          </Button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-heading font-bold text-slate-900 dark:text-white tracking-tight">
            Leads
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">Manage and track your sales leads</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto items-center">
          {/* Missed count badge */}
          {missedCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-full mr-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-700" data-testid="missed-count-header">
                {missedCount} Missed
              </span>
            </div>
          )}
          {canImport && (
            <Button
              onClick={() => setImportDialogOpen(true)}
              variant="outline"
              data-testid="import-leads-button"
              className="rounded-full px-4 py-2 border-green-200 text-green-700 hover:bg-green-50 w-full sm:w-auto"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Import Excel
            </Button>
          )}
          {/* Sync All Sources Button */}
          {(userRole === "admin" || userRole === "super_admin") && (
            <Button
              onClick={handleSyncAllSources}
              disabled={syncAllLoading}
              variant="outline"
              data-testid="sync-all-sources-button"
              className="rounded-full px-4 py-2 border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50 w-full sm:w-auto"
              title="Sync leads from all connected sources"
            >
              {syncAllLoading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {syncAllLoading ? "Syncing..." : "Sync All Sources"}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={async () => {
              // Fetch all columns (system + custom) with business-specific labels
              try {
                const res = await fetch(`${BACKEND_URL}/api/lead-table-preferences`, { credentials: "include" });
                if (res.ok) {
                  const data = await res.json();
                  const systemCols = (data.system_columns || []).map(c => ({ key: c.key, label: c.label }));
                  const customCols = (data.custom_columns || []).map(c => ({ key: c.key, label: c.label }));
                  // Add extra system fields not in SYSTEM_COLUMNS
                  const allCols = [...systemCols, ...customCols];
                  const allKeys = new Set(allCols.map(c => c.key));
                  [{ key: "assigned_to_name", label: "Assigned To" }, { key: "created_by_name", label: "Created By" }, { key: "remark", label: "Remark" }, { key: "notes", label: "Notes" }]
                    .forEach(c => { if (!allKeys.has(c.key)) allCols.push(c); });
                  setExportAvailableColumns(allCols);
                  setExportColumns(allCols.map(c => c.key));
                }
              } catch (e) { console.error(e); }
              setExportDialogOpen(true);
            }}
            className="rounded-full px-5 py-3 font-semibold border-emerald-300 text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Leads
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (open) {
                setNewLead({
                  name: "", email: "", phone: "", company: "", city: "",
                  stage: "New Lead", source: "", deal_value: "", notes: "",
                  next_meeting: "", meeting_notes: "",
                });
                setCustomFieldValues({});
              }
            }}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-lead-button"
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-3 font-semibold shadow-lg shadow-indigo-500/30 transition-all hover:scale-105 active:scale-95 w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-1rem)] max-w-md sm:max-w-lg">
              <DialogHeader className="sticky top-0 bg-white dark:bg-slate-800 pb-2 z-10">
                <DialogTitle className="text-xl sm:text-2xl font-heading font-bold text-slate-900 dark:text-white">
                  Add New Lead
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pb-4">
                <LeadFormFields
                  data={newLead}
                  onChange={setNewLead}
                  mode="create"
                  customFieldValues={customFieldValues}
                  onCustomFieldChange={(key, val) => setCustomFieldValues(prev => ({ ...prev, [key]: val }))}
                  stages={stages}
                  sources={LEAD_SOURCES}
                  businessUsers={businessUsers}
                  assignedTo={newLead.assigned_to || ""}
                  onAssignedToChange={(v) => setNewLead(prev => ({ ...prev, assigned_to: v }))}
                />
              
              <Button
                onClick={handleCreateLead}
                data-testid="create-lead-button"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold sm:sticky sm:bottom-0 mt-2"
              >
                Create Lead
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search and Filter Bar - Unified Filter Row */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
        {/* Row 1: Search and Primary Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {/* 1. Search */}
          <div className="relative flex-1 min-w-[180px] sm:min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="lead-search-input"
              placeholder="Search name, email, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-50 border border-slate-200 rounded-xl h-10"
            />
          </div>
          
          {/* 2. Lead Source Filter */}
          <Select value={sourceFilter} onValueChange={(value) => { setSourceFilter(value); setCurrentPage(1); }}>
            <SelectTrigger data-testid="source-filter-select" className="w-full sm:w-auto sm:max-w-[180px] bg-slate-50 border border-slate-200 rounded-xl h-10">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <SelectValue placeholder="Source" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources {leads.length > 0 && <span className="text-xs text-slate-400 ml-1">({leads.length})</span>}</SelectItem>
              {/* Show configured sources + any sources with actual leads */}
              {(() => {
                const allSources = new Set(LEAD_SOURCES);
                // Add any source that has a count but isn't in config
                Object.keys(sourceCounts).forEach(s => allSources.add(s));
                return Array.from(allSources).map((source) => (
                  <SelectItem key={source} value={source}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${sourceColor[source]?.split(' ')[0] || 'bg-slate-300'}`}></span>
                      {source}
                      {sourceCounts[source] > 0 && (
                        <span className="text-xs text-slate-400 ml-1">({sourceCounts[source]})</span>
                      )}
                    </div>
                  </SelectItem>
                ));
              })()}
            </SelectContent>
          </Select>
          
          {/* 3. Sort by User Filter - only for admin/manager roles */}
          {(userRole === "super_admin" || userRole === "admin" || userRole === "manager") && (
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger data-testid="user-filter-select" className="w-full sm:w-auto sm:max-w-[180px] bg-slate-50 border border-slate-200 rounded-xl h-10">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <SelectValue placeholder="User" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {businessUsers.map((user) => (
                  <SelectItem key={user.user_id} value={user.user_id}>
                    {user.name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* 4. Stage Filter Dropdown */}
          <Select value={stageFilter} onValueChange={(value) => { setStageFilter(value); setCurrentPage(1); }}>
            <SelectTrigger data-testid="stage-filter-select" className="w-full sm:w-auto sm:max-w-[200px] bg-slate-50 border border-slate-200 rounded-xl h-10">
              <div className="flex items-center gap-2 truncate">
                <Target className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="truncate"><SelectValue placeholder="All Stages" /></span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                  All Stages
                  <span className="text-xs text-slate-400 ml-1">({stageCounts.all || 0})</span>
                </div>
              </SelectItem>
              {stages.map((stage) => (
                <SelectItem key={stage} value={stage}>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${stageColor[stage]?.split(' ')[0] || 'bg-slate-300'}`}></span>
                    {stage}
                    {stageCounts[stage] > 0 && (
                      <span className="text-xs text-slate-400 ml-1">({stageCounts[stage]})</span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Row 2: Date Filters */}
        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2 text-slate-500 shrink-0">
            <Calendar className="w-4 h-4" />
            <span className="text-sm font-medium">Date Range:</span>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            <Input
              type="date"
              data-testid="start-date-filter"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setActiveDatePreset(""); }}
              className="w-36 bg-slate-50 border border-slate-200 rounded-xl h-10 text-sm px-3"
            />

            <span className="text-slate-400 text-sm font-medium shrink-0">&rarr;</span>

            <Input
              type="date"
              data-testid="end-date-filter"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setActiveDatePreset(""); }}
              className="w-36 bg-slate-50 border border-slate-200 rounded-xl h-10 text-sm px-3"
            />

            {/* Preset pills — same 6 shortcuts as /dashboard and /tasks */}
            <div className="flex items-center gap-1.5 ml-2 flex-wrap">
              {DATE_PRESETS.map(p => {
                const on = activeDatePreset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => {
                      const [s, e] = computeDatePreset(p.key);
                      setStartDate(s);
                      setEndDate(e);
                      setActiveDatePreset(p.key);
                    }}
                    data-testid={`lead-date-preset-${p.key}`}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                      on
                        ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                        : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-700"
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Clear Filters Button */}
          {(searchQuery || userFilter !== "all" || stageFilter !== "all" || sourceFilter !== "all" || startDate || endDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSourceFilter("all");
                setStageFilter("all");
                setSearchQuery("");
                setUserFilter("all");
                setStartDate("");
                setEndDate("");
                setActiveDatePreset("");
              }}
              data-testid="clear-all-filters"
              className="text-red-500 hover:text-red-600 hover:bg-red-50 text-xs h-8 px-3 ml-auto"
            >
              Clear All
            </Button>
          )}
        </div>
        
        {/* Active Filters Tags */}
        {(sourceFilter !== "all" || stageFilter !== "all" || userFilter !== "all" || startDate || endDate) && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
            <span className="text-xs text-slate-500">Active filters:</span>
            {sourceFilter !== "all" && (
              <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700 border border-blue-200">
                Source: {sourceFilter}
                <button onClick={() => setSourceFilter("all")} className="ml-1.5 hover:text-red-600">&times;</button>
              </Badge>
            )}
            {stageFilter !== "all" && (
              <Badge variant="secondary" className={`text-xs ${stageColor[stageFilter] || 'bg-slate-100 text-slate-700'}`}>
                Stage: {stageFilter}
                <button onClick={() => setStageFilter("all")} className="ml-1.5 hover:text-red-600">&times;</button>
              </Badge>
            )}
            {userFilter !== "all" && (
              <Badge variant="secondary" className="text-xs bg-purple-50 text-purple-700 border border-purple-200">
                User: {businessUsers.find(u => u.user_id === userFilter)?.name || 'Selected'}
                <button onClick={() => setUserFilter("all")} className="ml-1.5 hover:text-red-600">&times;</button>
              </Badge>
            )}
            {(startDate || endDate) && (
              <Badge variant="secondary" className="text-xs bg-amber-50 text-amber-700 border border-amber-200">
                Date: {startDate || '...'} → {endDate || '...'}
                <button onClick={() => { setStartDate(""); setEndDate(""); setActiveDatePreset(""); }} className="ml-1.5 hover:text-red-600">&times;</button>
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Today's Stats Analytics Row */}
      {todayStats.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Today</span>
            </div>
            <p className="text-2xl font-bold text-indigo-700">{todayStats.total}</p>
            <p className="text-xs text-indigo-600">New Leads</p>
          </div>
          {todayStats.bySource["Facebook"] > 0 && (
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">📘</span>
                <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Facebook</span>
              </div>
              <p className="text-2xl font-bold text-blue-700">{todayStats.bySource["Facebook"]}</p>
              <p className="text-xs text-blue-600">Today</p>
            </div>
          )}
          {todayStats.bySource["Google Sheet"] > 0 && (
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">📗</span>
                <span className="text-xs font-medium text-green-600 uppercase tracking-wide">Google Sheet</span>
              </div>
              <p className="text-2xl font-bold text-green-700">{todayStats.bySource["Google Sheet"]}</p>
              <p className="text-xs text-green-600">Today</p>
            </div>
          )}
          {todayStats.bySource["WhatsApp"] > 0 && (
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">💬</span>
                <span className="text-xs font-medium text-emerald-600 uppercase tracking-wide">WhatsApp</span>
              </div>
              <p className="text-2xl font-bold text-emerald-700">{todayStats.bySource["WhatsApp"]}</p>
              <p className="text-xs text-emerald-600">Today</p>
            </div>
          )}
        </div>
      )}
      
      {/* Results Summary and View Toggle */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Showing <strong>{filteredLeads.length}</strong> of <strong>{totalLeads}</strong> leads
          {selectedLeads.length > 0 && (
            <span className="ml-2 text-indigo-600">({selectedLeads.length} selected)</span>
          )}
        </p>
        
        {/* View Switcher + Column Customizer */}
        <div className="flex items-center gap-2">
          {effectiveView === "table" && (
            <ColumnCustomizer visibleColumns={visibleColumns} onColumnsChange={handleColumnsChange} />
          )}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1">
          <button
            data-testid="table-view-btn"
            onClick={() => handleViewChange("table")}
            className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              effectiveView === "table"
                ? "bg-indigo-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Table2 className="w-4 h-4" />
            <span className="hidden sm:inline">Table</span>
          </button>
          <button
            data-testid="list-view-btn"
            onClick={() => handleViewChange("list")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              effectiveView === "list"
                ? "bg-indigo-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <LayoutList className="w-4 h-4" />
            <span className="hidden sm:inline">List</span>
          </button>
          <button
            data-testid="grid-view-btn"
            onClick={() => handleViewChange("grid")}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              effectiveView === "grid"
                ? "bg-indigo-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="hidden sm:inline">Grid</span>
          </button>
          <button
            data-testid="kanban-view-btn"
            onClick={() => handleViewChange("kanban")}
            className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              effectiveView === "kanban"
                ? "bg-indigo-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Columns3 className="w-4 h-4" />
            <span className="hidden sm:inline">Kanban</span>
          </button>
        </div>
        </div>
      </div>
      
      {/* Bulk Actions Bar */}
      {selectedLeads.length > 0 && (
        <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
          <span className="text-sm font-medium text-indigo-700">
            {selectedLeads.length} lead(s) selected
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkDelete}
            className="border-red-300 text-red-600 hover:bg-red-50 rounded-lg"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete Selected
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedLeads([])}
            className="text-slate-500"
          >
            Clear Selection
          </Button>
        </div>
      )}

      {/* Table View */}
      {effectiveView === "table" && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <Checkbox
                      checked={selectedLeads.length === filteredLeads.length && filteredLeads.length > 0}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wide">S.No</th>
                  {orderedVisibleColumns.map(colKey => {
                    const f = fieldMap[colKey];
                    return (
                      <SortableColumnHeader
                        key={colKey}
                        columnKey={colKey}
                        label={f?.label || colKey}
                        sortable={f?.sortable ?? false}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        onHide={handleHideColumn}
                        allFields={allFieldsDef}
                        onReplace={handleReplaceColumn}
                      />
                    );
                  })}
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <TableSkeleton columns={orderedVisibleColumns.length} rows={6} />
                ) : sortedLeads.length === 0 ? (
                  <tr>
                    <td colSpan={orderedVisibleColumns.length + 3} className="px-4 py-12 text-center text-sm text-slate-500">
                      {leads.length === 0 
                        ? "No leads yet. Add your first lead to get started!"
                        : "No leads match your search criteria."}
                    </td>
                  </tr>
                ) : (
                  sortedLeads.map((lead, index) => (
                    <React.Fragment key={lead.lead_id}>
                      <tr 
                        data-testid={`table-row-${lead.lead_id}`}
                        onClick={() => setExpandedRowId(prev => prev === lead.lead_id ? null : lead.lead_id)}
                        className={`cursor-pointer transition-colors table-row-enter ${
                          expandedRowId === lead.lead_id ? "bg-indigo-50/50" : "hover:bg-slate-50"
                        } ${lead.is_missed ? "bg-red-50/50" : ""
                        } ${selectedLeads.includes(lead.lead_id) ? "bg-indigo-50" : ""}`}
                        style={{ animationDelay: `${index * 30}ms` }}
                      >
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1.5">
                            <ChevronRight
                              className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${expandedRowId === lead.lead_id ? "rotate-90 text-indigo-500" : ""}`}
                            />
                            <Checkbox
                              checked={selectedLeads.includes(lead.lead_id)}
                              onCheckedChange={() => handleSelectLead(lead.lead_id)}
                              data-testid={`select-lead-${lead.lead_id}`}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center text-sm font-medium text-slate-500">
                          {(currentPage - 1) * 20 + index + 1}
                        </td>
                        {orderedVisibleColumns.map(colKey => (
                          <DynamicCell key={colKey} colKey={colKey} lead={lead} stageColor={stageColor} sourceColor={sourceColor} customValues={allLeadCustomValues[lead.lead_id]} onViewLead={handleViewLead} />
                        ))}
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleViewLead(lead.lead_id)}
                              data-testid={`table-view-${lead.lead_id}`}
                              className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                              title="View Lead Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditLead(lead)}
                              data-testid={`table-edit-${lead.lead_id}`}
                              className="p-1.5 rounded-lg text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                              title="Edit Lead"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteLead(lead.lead_id, lead.name)}
                              data-testid={`table-delete-${lead.lead_id}`}
                              className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Delete Lead"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedRowId === lead.lead_id && (
                        <QuickViewRow lead={lead} colSpan={orderedVisibleColumns.length + 3} />
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* List View - Card Style */}
      {effectiveView === "list" && (
        <div className="space-y-4">
          {loading ? (
            <ListSkeleton count={5} />
          ) : filteredLeads.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
              <p className="text-sm text-slate-500">
                {leads.length === 0 
                  ? "No leads yet. Add your first lead to get started!"
                  : "No leads match your search criteria."}
              </p>
            </div>
          ) : (
            filteredLeads.map((lead, index) => {
              // Stage colors for badges - Updated with new stages
              const stageBadgeColors = {
                "New Lead": "bg-sky-100 text-sky-700 border border-sky-200",
                "Contacted": "bg-blue-100 text-blue-700 border border-blue-200",
                "Qualified": "bg-indigo-100 text-indigo-700 border border-indigo-200",
                "Requirement Received": "bg-violet-100 text-violet-700 border border-violet-200",
                "Quotation Sent": "bg-purple-100 text-purple-700 border border-purple-200",
                "Negotiation": "bg-amber-100 text-amber-700 border border-amber-200",
                "Follow-Up": "bg-orange-100 text-orange-700 border border-orange-200",
                "Client Converted": "bg-emerald-100 text-emerald-700 border border-emerald-200",
                "Payment Received": "bg-green-100 text-green-700 border border-green-200",
                "Client Onboarding": "bg-teal-100 text-teal-700 border border-teal-200",
                // Legacy stages for backward compatibility
                "Proposal Sent": "bg-violet-100 text-violet-700 border border-violet-200",
                "Follow-up": "bg-orange-100 text-orange-700 border border-orange-200",
                "Meeting Scheduled": "bg-cyan-100 text-cyan-700 border border-cyan-200",
                "Won / Converted": "bg-emerald-100 text-emerald-700 border border-emerald-200",
                "Lost": "bg-red-100 text-red-700 border border-red-200",
                "On Hold": "bg-slate-100 text-slate-600 border border-slate-200",
              };
              
              const sourceBadgeColors = {
                "Facebook": "bg-green-100 text-green-700 border border-green-200",
                "Facebook Ads": "bg-green-100 text-green-700 border border-green-200",
                "Instagram": "bg-pink-100 text-pink-700 border border-pink-200",
                "Google": "bg-blue-100 text-blue-700 border border-blue-200",
                "Google Ads": "bg-red-100 text-red-700 border border-red-200",
                "WhatsApp": "bg-emerald-100 text-emerald-700 border border-emerald-200",
                "Website": "bg-indigo-100 text-indigo-700 border border-indigo-200",
                "LinkedIn": "bg-blue-100 text-blue-700 border border-blue-200",
                "Referral": "bg-amber-100 text-amber-700 border border-amber-200",
                "Walk-in": "bg-purple-100 text-purple-700 border border-purple-200",
                "Manual": "bg-gray-100 text-gray-700 border border-gray-200",
              };
              
              return (
                <div
                  key={lead.lead_id}
                  data-testid={`lead-card-${lead.lead_id}`}
                  role="link"
                  tabIndex={0}
                  onClick={() => handleViewLead(lead.lead_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleViewLead(lead.lead_id); }
                  }}
                  className={`bg-white rounded-2xl border shadow-sm hover:shadow-md hover:border-indigo-200 hover:ring-1 hover:ring-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-all card-enter cursor-pointer ${
                    lead.is_missed
                      ? "border-red-200 bg-red-50/30"
                      : "border-slate-100"
                  }`}
                  style={{ animationDelay: `${index * 40}ms` }}
                >
                    <div className="p-4 sm:p-5">
                      {/* Header Row: Serial, Name, Badges */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        {/* Left: Serial, Name, Badges */}
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          {/* Serial Number */}
                          <span className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 bg-slate-100 text-slate-600 rounded-full text-xs sm:text-sm font-semibold">
                            {index + 1}
                          </span>
                          
                          {/* Lead Name */}
                          <h3 className={`text-base sm:text-lg font-bold ${
                            lead.is_missed ? "text-red-700" : "text-slate-800"
                          }`}>
                            {lead.name || lead.patient_name || lead.student_name || lead.company_name || lead.parent_name || lead.phone || "Unnamed"}
                          </h3>
                          
                          {/* Stage Badge */}
                          <Badge className={`text-[10px] sm:text-xs font-medium px-2 sm:px-3 py-0.5 sm:py-1 rounded-full ${
                            lead.is_missed 
                              ? "bg-red-100 text-red-700 border border-red-200"
                              : stageBadgeColors[lead.stage] || "bg-slate-100 text-slate-600 border border-slate-200"
                          }`}>
                            {lead.stage}
                          </Badge>
                          
                          {/* Source Badge */}
                          {lead.source && (
                            <Badge className={`text-[10px] sm:text-xs font-medium px-2 sm:px-3 py-0.5 sm:py-1 rounded-full ${
                              sourceBadgeColors[lead.source] || "bg-slate-100 text-slate-600 border border-slate-200"
                            }`}>
                              {lead.source}
                            </Badge>
                          )}
                          
                          {/* Deal Value */}
                          {lead.deal_value && (
                            <Badge className="text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                              ₹{formatCurrency(lead.deal_value)}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Right: Action Buttons — stopPropagation so card-click doesn't fire when these are tapped */}
                        <div
                          className="flex items-center gap-1 sm:gap-2 flex-wrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditLead(lead); }}
                            data-testid={`edit-lead-${lead.lead_id}`}
                            className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl border border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-colors"
                            title="Edit Lead"
                          >
                            <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteLead(lead.lead_id, lead.name); }}
                            data-testid={`delete-lead-${lead.lead_id}`}
                            className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors"
                            title="Delete Lead"
                          >
                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleScoreLead(lead.lead_id); }}
                            data-testid={`score-lead-${lead.lead_id}`}
                            className="flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-slate-200 text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-colors text-xs sm:text-sm font-medium"
                            title="AI Score"
                          >
                            <Brain className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">Score</span>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSuggestFollowup(lead.lead_id); }}
                            data-testid={`suggest-lead-${lead.lead_id}`}
                            className="flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-slate-200 text-violet-600 hover:border-violet-200 hover:bg-violet-50 transition-colors text-xs sm:text-sm font-medium"
                            title="AI Suggest"
                          >
                            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">Suggest</span>
                          </button>
                        </div>
                      </div>
                      
                      {/* Contact Details */}
                      <div className="space-y-2 mb-4">
                        {lead.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="w-4 h-4 text-indigo-500" />
                            <a href={`mailto:${lead.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-indigo-600 hover:underline">
                              {lead.email}
                            </a>
                          </div>
                        )}
                        {lead.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-4 h-4 text-indigo-500" />
                            <a href={`tel:${lead.phone}`} onClick={(e) => e.stopPropagation()} className="hover:text-indigo-600 hover:underline">
                              {lead.phone}
                            </a>
                          </div>
                        )}
                        {getCompany(lead, allLeadCustomValues?.[lead.lead_id]) && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <FileSpreadsheet className="w-4 h-4 text-indigo-500" />
                            <span>{getCompany(lead, allLeadCustomValues?.[lead.lead_id])}</span>
                          </div>
                        )}
                        {lead.city && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="w-4 h-4 text-indigo-500" />
                            <span>{lead.city}</span>
                          </div>
                        )}
                        {lead.notes && (
                          <div className="flex items-start gap-2 text-sm text-slate-600">
                            <MessageSquare className="w-4 h-4 text-violet-500 mt-0.5" />
                            <span className="text-slate-500">{lead.notes}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Next Meeting / Follow-up Box */}
                      {lead.next_meeting && (
                        <div className={`rounded-xl p-4 mb-4 ${
                          lead.is_missed 
                            ? "bg-red-50 border border-red-200" 
                            : "bg-amber-50 border border-amber-200"
                        }`}>
                          <div className={`flex items-center gap-2 text-sm font-medium mb-1 ${
                            lead.is_missed ? "text-red-700" : "text-amber-700"
                          }`}>
                            <Calendar className="w-4 h-4" />
                            Next Meeting / Follow-up
                            {lead.is_missed && (
                              <Badge className="ml-2 bg-red-100 text-red-700 text-xs">MISSED</Badge>
                            )}
                          </div>
                          <div className={`text-base font-semibold ${
                            lead.is_missed ? "text-red-800" : "text-amber-800"
                          }`}>
                            {format(new Date(lead.next_meeting), "MMMM do, yyyy 'at' h:mm a")}
                          </div>
                          {lead.is_missed && (
                            <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); openMeetingActionDialog(lead, "complete", e); }}
                                data-testid={`complete-lead-${lead.lead_id}`}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Mark Complete
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); openMeetingActionDialog(lead, "reschedule", e); }}
                                data-testid={`reschedule-lead-${lead.lead_id}`}
                                className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs"
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Reschedule
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Footer: Created by, Date, Time ago */}
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-[10px] sm:text-xs text-slate-400 pt-2 border-t border-slate-100">
                        {lead.created_by_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            <span className="hidden xs:inline">Created by:</span> {lead.created_by_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span className="hidden xs:inline">Created:</span> {format(new Date(lead.created_at), "MMM dd, yyyy")}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {getLeadAge(lead.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
      )}

      {/* Grid View */}
      {effectiveView === "grid" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {loading ? (
              <GridSkeleton count={8} />
            ) : filteredLeads.length === 0 ? (
              <div className="col-span-full">
                <Card className="bg-white rounded-2xl border border-slate-100">
                  <CardContent className="p-8 text-center">
                    <p className="text-sm text-slate-500">
                      {leads.length === 0 
                        ? "No leads yet. Add your first lead to get started!"
                        : "No leads match your search criteria."}
                    </p>
                  </CardContent>
                </Card>
              </div>
            ) : (
              filteredLeads.map((lead, index) => {
                // Stage badge colors - Updated with new stages
                const stageBadgeColors = {
                  "New Lead": "bg-sky-100 text-sky-700 border border-sky-200",
                  "Contacted": "bg-blue-100 text-blue-700 border border-blue-200",
                  "Qualified": "bg-indigo-100 text-indigo-700 border border-indigo-200",
                  "Requirement Received": "bg-violet-100 text-violet-700 border border-violet-200",
                  "Quotation Sent": "bg-purple-100 text-purple-700 border border-purple-200",
                  "Negotiation": "bg-amber-100 text-amber-700 border border-amber-200",
                  "Follow-Up": "bg-orange-100 text-orange-700 border border-orange-200",
                  "Client Converted": "bg-emerald-100 text-emerald-700 border border-emerald-200",
                  "Payment Received": "bg-green-100 text-green-700 border border-green-200",
                  "Client Onboarding": "bg-teal-100 text-teal-700 border border-teal-200",
                  // Legacy stages for backward compatibility
                  "Proposal Sent": "bg-violet-100 text-violet-700 border border-violet-200",
                  "Follow-up": "bg-orange-100 text-orange-700 border border-orange-200",
                  "Meeting Scheduled": "bg-cyan-100 text-cyan-700 border border-cyan-200",
                  "Won / Converted": "bg-emerald-100 text-emerald-700 border border-emerald-200",
                  "Lost": "bg-red-100 text-red-700 border border-red-200",
                  "On Hold": "bg-slate-100 text-slate-600 border border-slate-200",
                };
                
                const sourceBadgeColors = {
                  "Facebook": "bg-green-100 text-green-700 border border-green-200",
                  "Facebook Ads": "bg-green-100 text-green-700 border border-green-200",
                  "Instagram": "bg-pink-100 text-pink-700 border border-pink-200",
                  "Google": "bg-blue-100 text-blue-700 border border-blue-200",
                  "Google Ads": "bg-red-100 text-red-700 border border-red-200",
                  "WhatsApp": "bg-emerald-100 text-emerald-700 border border-emerald-200",
                  "Website": "bg-indigo-100 text-indigo-700 border border-indigo-200",
                  "LinkedIn": "bg-blue-100 text-blue-700 border border-blue-200",
                  "Referral": "bg-orange-100 text-orange-700 border border-orange-200",
                  "Walk-in": "bg-cyan-100 text-cyan-700 border border-cyan-200",
                  "Manual": "bg-gray-100 text-gray-600 border border-gray-200",
                };
                
                return (
                  <div
                    key={lead.lead_id}
                    data-testid={`grid-card-${lead.lead_id}`}
                    className={`bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all cursor-pointer card-enter ${
                      lead.is_missed 
                        ? "border-red-200 bg-red-50/30" 
                        : "border-slate-100"
                    }`}
                    onClick={() => handleEditLead(lead)}
                    style={{ animationDelay: `${index * 40}ms` }}
                  >
                    <div className="p-4">
                      {/* Header: Serial + Name + Score */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm font-medium text-indigo-600">
                          {index + 1}
                        </span>
                        <h4 className={`font-bold text-base truncate flex-1 ${
                          lead.is_missed ? "text-red-700" : "text-slate-800"
                        }`}>
                          {lead.name || lead.patient_name || lead.student_name || lead.company_name || lead.parent_name || lead.phone || "Unnamed"}
                        </h4>
                        {lead.score && (
                          <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">
                            {lead.score}
                          </span>
                        )}
                      </div>
                      
                      {/* Badges: Stage + Source */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Badge className={`text-xs font-medium px-3 py-1 rounded-full ${
                          lead.is_missed 
                            ? "bg-red-100 text-red-700 border border-red-200"
                            : stageBadgeColors[lead.stage] || "bg-slate-100 text-slate-600 border border-slate-200"
                        }`}>
                          {lead.stage}
                        </Badge>
                        {lead.source && (
                          <Badge className={`text-xs font-medium px-3 py-1 rounded-full ${
                            sourceBadgeColors[lead.source] || "bg-slate-100 text-slate-600 border border-slate-200"
                          }`}>
                            {lead.source}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Contact Info */}
                      <div className="space-y-1.5 mb-3">
                        {getCompany(lead, allLeadCustomValues?.[lead.lead_id]) && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <FileSpreadsheet className="w-4 h-4 text-indigo-400" />
                            <span className="truncate">{getCompany(lead, allLeadCustomValues?.[lead.lead_id])}</span>
                          </div>
                        )}
                        {lead.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="w-4 h-4 text-indigo-400" />
                            <span className="truncate">{lead.email}</span>
                          </div>
                        )}
                        {lead.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-4 h-4 text-indigo-400" />
                            <span>{lead.phone}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Deal Value Row */}
                      {lead.deal_value && (
                        <div className="bg-emerald-50 rounded-lg px-3 py-2 mb-2">
                          <span className="text-sm font-semibold text-emerald-700">
                            ₹{formatCurrency(lead.deal_value)}
                          </span>
                        </div>
                      )}
                      
                      {/* Next Meeting Row */}
                      {lead.next_meeting && (
                        <div className={`rounded-lg px-3 py-2 mb-3 ${
                          lead.is_missed 
                            ? "bg-red-50" 
                            : "bg-amber-50"
                        }`}>
                          <div className={`flex items-center gap-2 text-sm font-medium ${
                            lead.is_missed ? "text-red-700" : "text-amber-700"
                          }`}>
                            <Calendar className="w-4 h-4" />
                            <span>{format(new Date(lead.next_meeting), "MMM dd, h:mm a")}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Footer: Creator + Time */}
                      <div className="flex items-center justify-between text-xs text-slate-400 pt-2 border-t border-slate-100">
                        {lead.created_by_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {lead.created_by_name}
                          </span>
                        )}
                        <span>{getLeadAge(lead.created_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
      )}

      {effectiveView === "kanban" && (
        <LeadsKanbanView
          leads={filteredLeads}
          stages={stages}
          stageColor={stageColor}
          onCardClick={(lead) => handleViewLead(lead.lead_id)}
          onStageChange={handleKanbanStageChange}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-100 shadow-sm px-5 py-3 mt-4">
          <p className="text-sm text-slate-500">
            Showing <span className="font-semibold text-slate-700">{(currentPage - 1) * leadsPerPage + 1}-{Math.min(currentPage * leadsPerPage, totalLeads)}</span> of <span className="font-semibold text-slate-700">{totalLeads}</span> leads
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="rounded-lg h-8 px-2">
              First
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="rounded-lg h-8 px-3">
              Previous
            </Button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page;
              if (totalPages <= 5) { page = i + 1; }
              else if (currentPage <= 3) { page = i + 1; }
              else if (currentPage >= totalPages - 2) { page = totalPages - 4 + i; }
              else { page = currentPage - 2 + i; }
              return (
                <Button key={page} variant={page === currentPage ? "default" : "outline"} size="sm"
                  onClick={() => setCurrentPage(page)}
                  className={`rounded-lg h-8 w-8 p-0 ${page === currentPage ? "bg-indigo-600 text-white hover:bg-indigo-700" : ""}`}>
                  {page}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="rounded-lg h-8 px-3">
              Next
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="rounded-lg h-8 px-2">
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Edit Lead Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-1rem)] max-w-md sm:max-w-lg">
          <DialogHeader className="sticky top-0 bg-white dark:bg-slate-800 pb-2 z-10">
            <DialogTitle className="text-xl sm:text-2xl font-heading font-bold text-slate-900 dark:text-white">
              Edit Lead
            </DialogTitle>
          </DialogHeader>
          {editingLead && (
            <div className="space-y-4 pb-4">
              <LeadFormFields
                data={editingLead}
                onChange={setEditingLead}
                mode="edit"
                customFieldValues={customFieldValues}
                onCustomFieldChange={(key, val) => setCustomFieldValues(prev => ({ ...prev, [key]: val }))}
                stages={stages}
                sources={LEAD_SOURCES}
                businessUsers={businessUsers}
                assignedTo={editingLead.assigned_to}
                onAssignedToChange={(v) => setEditingLead({ ...editingLead, assigned_to: v })}
              />
              
              {/* Lead Remarks Section */}
              <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="lead-remarks-section">
                <button
                  type="button"
                  onClick={() => setShowRemarks(!showRemarks)}
                  className="w-full flex items-center justify-between p-3 bg-amber-50 hover:bg-amber-100 transition-colors"
                  data-testid="toggle-remarks-btn"
                >
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-semibold text-amber-800">Lead Remarks</span>
                    {remarks.length > 0 && (
                      <Badge className="bg-amber-200 text-amber-800 text-xs">{remarks.length}</Badge>
                    )}
                  </div>
                  {showRemarks ? <ChevronUp className="w-4 h-4 text-amber-600" /> : <ChevronDown className="w-4 h-4 text-amber-600" />}
                </button>
                
                {showRemarks && (
                  <div className="p-3 space-y-3 bg-white">
                    {/* Add Remark Input */}
                    <div className="flex gap-2">
                      <Input
                        value={newRemark}
                        onChange={(e) => setNewRemark(e.target.value)}
                        placeholder="Add a remark..."
                        className="flex-1 text-sm"
                        data-testid="remark-input"
                        onKeyDown={(e) => { if (e.key === "Enter") handleAddRemark(); }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleAddRemark}
                        className="bg-amber-500 hover:bg-amber-400 text-white"
                        data-testid="add-remark-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Remarks List */}
                    {loadingRemarks ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                      </div>
                    ) : remarks.length === 0 ? (
                      <p className="text-center text-xs text-slate-400 py-3">No remarks yet</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {remarks.map((r) => (
                          <div key={r.remark_id} className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-lg group" data-testid={`remark-${r.remark_id}`}>
                            <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <User className="w-3 h-3 text-amber-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-700">{r.text}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] text-slate-400">{r.created_by_name}</span>
                                <span className="text-[10px] text-slate-300">&middot;</span>
                                <span className="text-[10px] text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteRemark(r.remark_id)}
                              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-all"
                              data-testid={`delete-remark-${r.remark_id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Follow-up History Timeline Section */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowTimeline(!showTimeline)}
                  data-testid="toggle-timeline-btn"
                  className="w-full p-3 bg-slate-50 hover:bg-slate-100 flex items-center justify-between transition-colors"
                >
                  <span className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
                    <History className="w-4 h-4 text-indigo-600" />
                    Follow-up History
                    {activities.length > 0 && (
                      <Badge className="bg-indigo-100 text-indigo-700 text-xs">{activities.length}</Badge>
                    )}
                  </span>
                  <ArrowRight className={`w-4 h-4 text-slate-400 transition-transform ${showTimeline ? 'rotate-90' : ''}`} />
                </button>
                
                {showTimeline && (
                  <div className="p-3 space-y-4 max-h-[300px] overflow-y-auto bg-white">
                    {/* Add New Activity Form */}
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl space-y-3">
                      <Label className="text-indigo-700 font-semibold text-sm">Add Follow-up Entry</Label>
                      <div className="flex gap-2">
                        <Select
                          value={newActivity.action_type}
                          onValueChange={(value) => setNewActivity({ ...newActivity, action_type: value })}
                        >
                          <SelectTrigger data-testid="activity-type-select" className="w-[120px] bg-white border border-indigo-200 rounded-lg text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="call">
                              <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> Call</span>
                            </SelectItem>
                            <SelectItem value="meeting">
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Meeting</span>
                            </SelectItem>
                            <SelectItem value="email">
                              <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email</span>
                            </SelectItem>
                            <SelectItem value="note">
                              <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Note</span>
                            </SelectItem>
                            <SelectItem value="follow_up">
                              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Follow-up</span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Description..."
                          data-testid="activity-description-input"
                          value={newActivity.description}
                          onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                          className="flex-1 bg-white border border-indigo-200 rounded-lg text-xs"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Additional remarks (optional)..."
                          data-testid="activity-remarks-input"
                          value={newActivity.remarks}
                          onChange={(e) => setNewActivity({ ...newActivity, remarks: e.target.value })}
                          className="flex-1 bg-white border border-indigo-200 rounded-lg text-xs"
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleAddActivity}
                          data-testid="add-activity-btn"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs px-4"
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                    
                    {/* Activity Timeline */}
                    {loadingActivities ? (
                      <div className="text-center py-4 text-slate-500 text-sm">Loading history...</div>
                    ) : activities.length === 0 ? (
                      <div className="text-center py-4 text-slate-500 text-sm">No follow-up history yet</div>
                    ) : (
                      <div className="relative pl-4 border-l-2 border-slate-200 space-y-4">
                        {activities.map((activity) => (
                          <div 
                            key={activity.activity_id} 
                            data-testid={`activity-${activity.activity_id}`}
                            className="relative"
                          >
                            {/* Timeline dot */}
                            <div className={`absolute -left-[21px] w-3 h-3 rounded-full border-2 border-white ${
                              activity.action_type === 'call' ? 'bg-green-500' :
                              activity.action_type === 'meeting' ? 'bg-amber-500' :
                              activity.action_type === 'email' ? 'bg-blue-500' :
                              activity.action_type === 'status_change' ? 'bg-purple-500' :
                              activity.action_type === 'created' ? 'bg-indigo-500' :
                              activity.action_type === 'follow_up' ? 'bg-teal-500' :
                              'bg-slate-400'
                            }`} />
                            
                            <div className="bg-slate-50 rounded-lg p-3 ml-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  {activity.action_type === 'call' && <Phone className="w-3 h-3 text-green-600" />}
                                  {activity.action_type === 'meeting' && <Calendar className="w-3 h-3 text-amber-600" />}
                                  {activity.action_type === 'email' && <Mail className="w-3 h-3 text-blue-600" />}
                                  {activity.action_type === 'status_change' && <ArrowRight className="w-3 h-3 text-purple-600" />}
                                  {activity.action_type === 'note' && <MessageSquare className="w-3 h-3 text-slate-600" />}
                                  {activity.action_type === 'created' && <Plus className="w-3 h-3 text-indigo-600" />}
                                  {activity.action_type === 'follow_up' && <CheckCircle className="w-3 h-3 text-teal-600" />}
                                  <Badge className={`text-[10px] px-2 py-0 ${
                                    activity.action_type === 'call' ? 'bg-green-100 text-green-700' :
                                    activity.action_type === 'meeting' ? 'bg-amber-100 text-amber-700' :
                                    activity.action_type === 'email' ? 'bg-blue-100 text-blue-700' :
                                    activity.action_type === 'status_change' ? 'bg-purple-100 text-purple-700' :
                                    activity.action_type === 'created' ? 'bg-indigo-100 text-indigo-700' :
                                    activity.action_type === 'follow_up' ? 'bg-teal-100 text-teal-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`}>
                                    {activity.action_type.replace('_', ' ')}
                                  </Badge>
                                </div>
                                <span className="text-[10px] text-slate-400 whitespace-nowrap">
                                  {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                                </span>
                              </div>
                              <p className="text-xs text-slate-700 mt-1">{activity.description}</p>
                              {activity.remarks && (
                                <p className="text-[10px] text-slate-500 mt-1 italic">"{activity.remarks}"</p>
                              )}
                              <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {activity.created_by_name} • {format(new Date(activity.created_at), "MMM d, yyyy 'at' h:mm a")}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Button row: sticky on desktop, natural end-of-form on mobile so it never overlaps inputs */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:sticky sm:bottom-0 bg-white dark:bg-slate-800 pt-4 sm:pt-2 pb-1 mt-2 border-t sm:border-t-0 border-slate-100 dark:border-slate-700">
                <Button
                  onClick={handleUpdateLead}
                  data-testid="update-lead-button"
                  className="w-full sm:flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold"
                >
                  Update Lead
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                  className="w-full sm:flex-1 rounded-full border-slate-200"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Import Dialog */}
      {/* Export Leads Modal */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl w-[calc(100vw-1rem)] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Download className="w-5 h-5 text-emerald-600" />
              Export Leads
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            {/* Format Selection */}
            <div>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Export Format</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "csv", label: "CSV", desc: "Fast, lightweight", icon: "📄" },
                  { id: "xlsx", label: "Excel", desc: "Recommended", icon: "📊" },
                ].map(f => (
                  <button key={f.id} onClick={() => setExportFormat(f.id)}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${exportFormat === f.id ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" : "border-slate-200 dark:border-slate-700 hover:border-slate-300"}`}>
                    <span className="text-2xl">{f.icon}</span>
                    <p className="text-sm font-semibold mt-1">{f.label}</p>
                    <p className="text-[10px] text-slate-400">{f.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Column Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Select Columns</p>
                <div className="flex gap-2">
                  <button onClick={() => setExportColumns(exportAvailableColumns.map(c => c.key))}
                    className="text-xs text-indigo-600 hover:underline">Select All</button>
                  <button onClick={() => setExportColumns([])}
                    className="text-xs text-slate-400 hover:underline">Clear</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-[250px] overflow-y-auto">
                {exportAvailableColumns.map(col => (
                  <label key={col.key} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${exportColumns.includes(col.key) ? "bg-emerald-50 dark:bg-emerald-900/20" : "hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                    <input type="checkbox" checked={exportColumns.includes(col.key)}
                      onChange={(e) => {
                        if (e.target.checked) setExportColumns(prev => [...prev, col.key]);
                        else setExportColumns(prev => prev.filter(c => c !== col.key));
                      }}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                    <span className="text-sm">{col.label}</span>
                  </label>
                ))}
              </div>
              {exportColumns.length === 0 && <p className="text-xs text-red-500 mt-1">Select at least 1 column</p>}
            </div>

            {/* Active Filters Info */}
            {(sourceFilter !== "all" || stageFilter !== "all" || startDate || endDate || searchQuery) && (
              <div className="bg-amber-50 dark:bg-amber-900/10 rounded-xl p-3 text-xs text-amber-700">
                <p className="font-semibold mb-1">Active Filters Applied:</p>
                {sourceFilter !== "all" && <span className="inline-block bg-amber-100 px-2 py-0.5 rounded mr-1 mb-1">Source: {sourceFilter}</span>}
                {stageFilter !== "all" && <span className="inline-block bg-amber-100 px-2 py-0.5 rounded mr-1 mb-1">Stage: {stageFilter}</span>}
                {startDate && <span className="inline-block bg-amber-100 px-2 py-0.5 rounded mr-1 mb-1">From: {startDate}</span>}
                {endDate && <span className="inline-block bg-amber-100 px-2 py-0.5 rounded mr-1 mb-1">To: {endDate}</span>}
                {searchQuery && <span className="inline-block bg-amber-100 px-2 py-0.5 rounded mr-1 mb-1">Search: {searchQuery}</span>}
              </div>
            )}

            {/* Export Button */}
            <div className="flex gap-2 pt-1">
              <Button
                disabled={exportColumns.length === 0 || exporting}
                onClick={async () => {
                  setExporting(true);
                  try {
                    const params = new URLSearchParams();
                    if (businessId) params.append("business_id", businessId);
                    if (sourceFilter && sourceFilter !== "all") params.append("source", sourceFilter);
                    if (stageFilter && stageFilter !== "all") params.append("stage", stageFilter);
                    if (startDate) params.append("start_date", startDate);
                    if (endDate) params.append("end_date", endDate);
                    if (searchQuery) params.append("search", searchQuery);
                    params.append("format", exportFormat);
                    params.append("columns", exportColumns.join(","));
                    const res = await fetch(`${BACKEND_URL}/api/leads/export?${params.toString()}`, { credentials: "include" });
                    if (res.ok) {
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      const ext = exportFormat === "pdf" ? "pdf" : exportFormat === "csv" ? "csv" : "xlsx";
                      a.download = `leads_export_${new Date().toISOString().split("T")[0]}.${ext}`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success(`Exported ${totalLeads} leads as ${exportFormat.toUpperCase()}`);
                      setExportDialogOpen(false);
                    } else {
                      const err = await res.json().catch(() => ({}));
                      toast.error(err.detail || "Export failed");
                    }
                  } catch { toast.error("Export failed"); }
                  finally { setExporting(false); }
                }}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11"
              >
                {exporting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Preparing export...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> Export {totalLeads} Leads as {exportFormat.toUpperCase()}</>
                )}
              </Button>
              <Button variant="outline" onClick={() => setExportDialogOpen(false)} className="rounded-xl h-11">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <LeadImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImportComplete={() => fetchLeads()}
      />
      
      {/* Meeting Action Dialog */}
      <Dialog open={meetingActionOpen} onOpenChange={setMeetingActionOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 rounded-2xl w-[calc(100vw-1rem)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
              {meetingActionType === "complete" ? (
                <>
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  Mark Meeting Complete
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 text-amber-600" />
                  Reschedule Meeting
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {actionLead && (
            <div className="space-y-4">
              {/* Lead info */}
              <div className={`p-3 rounded-xl ${actionLead.is_missed ? "bg-red-50" : "bg-slate-50"}`}>
                <p className={`font-semibold ${actionLead.is_missed ? "text-red-800" : "text-slate-900"}`}>{actionLead.name}</p>
                {actionLead.next_meeting && (
                  <p className="text-sm text-slate-500">
                    Scheduled: {format(new Date(actionLead.next_meeting), "PPP 'at' p")}
                  </p>
                )}
                {actionLead.is_missed && (
                  <Badge className="mt-2 bg-red-100 text-red-700 text-xs">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    This meeting was missed
                  </Badge>
                )}
              </div>
              
              {/* Reschedule date picker */}
              {meetingActionType === "reschedule" && (
                <div>
                  <Label htmlFor="lead-reschedule-date" className="text-sm font-medium text-slate-700">
                    New Date & Time *
                  </Label>
                  <Input
                    id="lead-reschedule-date"
                    type="datetime-local"
                    data-testid="lead-reschedule-date-input"
                    value={rescheduleDate}
                    onChange={(e) => setRescheduleDate(e.target.value)}
                    className="mt-1 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              )}
              
              {/* Remarks */}
              <div>
                <Label htmlFor="lead-action-remarks" className="text-sm font-medium text-slate-700">
                  {meetingActionType === "complete" ? "Outcome / Notes" : "Reason for Rescheduling"}
                </Label>
                <Textarea
                  id="lead-action-remarks"
                  data-testid="lead-action-remarks-input"
                  placeholder={meetingActionType === "complete" ? "How did the meeting go?" : "Why are you rescheduling?"}
                  value={actionRemarks}
                  onChange={(e) => setActionRemarks(e.target.value)}
                  className="mt-1 bg-slate-50 border border-slate-200 rounded-xl"
                  rows={3}
                />
              </div>
              
              {/* Action buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleMeetingAction}
                  data-testid="lead-confirm-action-btn"
                  className={`flex-1 rounded-full py-3 font-semibold ${
                    meetingActionType === "complete" 
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white" 
                      : "bg-amber-600 hover:bg-amber-700 text-white"
                  }`}
                >
                  {meetingActionType === "complete" ? "Mark as Complete" : "Reschedule Meeting"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setMeetingActionOpen(false)}
                  className="flex-1 rounded-full border-slate-200"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sync All Sources Result Dialog */}
      <Dialog open={showSyncResult} onOpenChange={setShowSyncResult}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              <RefreshCw className="w-5 h-5 text-indigo-600" /> Sync Complete
            </DialogTitle>
          </DialogHeader>
          {syncResult && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{syncResult.totalCreated}</p>
                  <p className="text-xs text-slate-500">Leads Created</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{syncResult.totalUpdated}</p>
                  <p className="text-xs text-slate-500">Leads Updated</p>
                </div>
              </div>
              {syncResult.errors.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-700 mb-1">Sync Errors:</p>
                  <ul className="text-xs text-red-600 list-disc list-inside">
                    {syncResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowSyncResult(false)} className="w-full rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
